import {
  InterpreterEntities,
  useInterpreterStore,
} from "@coltorapps/builder-react";
import { Button } from "./button";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FormDefinition, User } from "@/types/domain";
import { InterpreterStoreData, Schema } from "@coltorapps/builder";
import { basicFormBuilder } from "../form/builder/definition";
import { entitiesComponents } from "@/const/form-builder";
import useValidatorStore from "@/hooks/useValidatorStore";
import { ZodError } from "zod";
import { useToast } from "./toast";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCodeHelper } from "@/hooks/useCode/useCodeHelper";
import { usePreloadMasterDataForExpressions } from "@/hooks/useMasterData";
import { useReactiveDefaultValues } from "@/hooks/useReactiveDefaultValues";
import { flushFormCommits } from "@/utils/form-commit-registry";
import { Application } from "@/types/application";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  defaultValueLoadingIdsAtom,
  interpreterStoreAtom,
  runtimeApplicationAtom,
  runtimeFormAtom,
  selectedApplicantAtom,
  userAtom,
} from "@/store";
import UserSelect from "./select/user-id-select";
import { ApplicationSummary } from "./ApplicationSummary";

type Props = {
  form: Partial<FormDefinition>;
  bindingId?: string;
  draftCacheKey?: string;
  formSchema: Schema<typeof basicFormBuilder>;
  onSave: (
    values: InterpreterStoreData<typeof basicFormBuilder>,
    isDirty?: boolean,
  ) => void;
  onSaveDraft: (
    values: InterpreterStoreData<typeof basicFormBuilder>,
    isDirty?: boolean,
  ) => void;
  isDirty?: boolean;
  setIsDirty?: Dispatch<SetStateAction<boolean>>;
  isSubmitting?: boolean;
  initialData?: Record<string, any>;
  application?: Application;
  onApplicantChange?: (user: User | null) => void;
};

type SchemaEntity = {
  parentId?: string;
  children?: string[];
  attributes?: Record<string, unknown>;
};

type GridHeaderAttributeItem = {
  hide?: boolean;
};

type ApplicationFormDraft = {
  values: InterpreterStoreData<typeof basicFormBuilder>["entitiesValues"];
};

const applicationFormDrafts = new Map<string, ApplicationFormDraft>();

export function getApplicationFormDraftCacheKey({
  bindingId,
  formId,
  schemaSignature,
}: {
  bindingId?: string;
  formId?: string;
  schemaSignature: string;
}) {
  return `${bindingId ?? formId ?? "application-form"}:${schemaSignature}`;
}

export function clearApplicationFormDraft(cacheKey: string) {
  applicationFormDrafts.delete(cacheKey);
}

function buildVisibleFormSchema(
  formSchema: Schema<typeof basicFormBuilder>,
): Schema<typeof basicFormBuilder> {
  const entities = formSchema?.entities ?? {};
  const root = formSchema?.root ?? [];
  const hiddenEntityIds = new Set<string>();

  Object.entries(entities).forEach(([entityId, entity]) => {
    const attributes = (entity?.attributes ?? {}) as Record<string, unknown>;
    if (attributes.hide === true) {
      hiddenEntityIds.add(entityId);
    }
  });

  let changed = false;
  let hasParentPropagation = true;
  while (hasParentPropagation) {
    hasParentPropagation = false;
    Object.entries(entities).forEach(([entityId, entity]) => {
      const parentId = (entity as SchemaEntity).parentId;
      if (
        parentId &&
        hiddenEntityIds.has(parentId) &&
        !hiddenEntityIds.has(entityId)
      ) {
        hiddenEntityIds.add(entityId);
        hasParentPropagation = true;
      }
    });
  }

  const nextEntities = Object.fromEntries(
    Object.entries(entities)
      .filter(([entityId]) => !hiddenEntityIds.has(entityId))
      .map(([entityId, entity]) => {
        const schemaEntity = entity as SchemaEntity;
        const nextAttributes = {
          ...(schemaEntity.attributes ?? {}),
        } as Record<string, unknown>;
        const nextEntity: SchemaEntity = {
          ...schemaEntity,
          attributes: nextAttributes,
        };

        if (Array.isArray(schemaEntity.children)) {
          const nextChildren = schemaEntity.children.filter(
            (childId) => !hiddenEntityIds.has(childId),
          );
          if (nextChildren.length !== schemaEntity.children.length) {
            changed = true;
          }
          nextEntity.children = nextChildren;
        }

        const slotMapping = nextAttributes.slotMapping;
        if (slotMapping && typeof slotMapping === "object") {
          const nextSlotMapping = Object.fromEntries(
            Object.entries(slotMapping as Record<string, unknown>).filter(
              ([childId]) => !hiddenEntityIds.has(childId),
            ),
          );
          if (
            Object.keys(nextSlotMapping).length !==
            Object.keys(slotMapping as Record<string, unknown>).length
          ) {
            changed = true;
          }
          nextAttributes.slotMapping = nextSlotMapping;
        }

        const gridHeaders = nextAttributes.gridHeaders;
        if (Array.isArray(gridHeaders)) {
          const visibleGridHeaders = gridHeaders.filter((header) => {
            const item = header as GridHeaderAttributeItem;
            return item.hide !== true;
          });
          if (visibleGridHeaders.length !== gridHeaders.length) {
            changed = true;
          }
          nextAttributes.gridHeaders = visibleGridHeaders;
        }

        return [entityId, nextEntity];
      }),
  );

  const nextRoot = root.filter((entityId) => !hiddenEntityIds.has(entityId));
  if (nextRoot.length !== root.length || hiddenEntityIds.size > 0) {
    changed = true;
  }

  if (!changed) {
    return formSchema;
  }

  return {
    ...formSchema,
    entities: nextEntities as typeof formSchema.entities,
    root: nextRoot,
  };
}
/**
 * This form will check the passed-in schema and remove all the component that has (hide: true) attribute
 *
 */
export default function ApplicationForm({
  form,
  bindingId,
  draftCacheKey,
  formSchema,
  isSubmitting = false,
  onSave,
  onSaveDraft,
  isDirty,
  setIsDirty,
  initialData,
  application,
  onApplicantChange,
}: Props) {
  const { t } = useTranslation();
  const {
    executeAllRegistry,
    executeAllLocalValidator,
    executeFormValidator,
    initiateValidatorStore,
  } = useValidatorStore();
  const { toast } = useToast();
  const appliedInitialDataKeyRef = useRef<string | null>(null);
  const initialDataSignature = useMemo(
    () => JSON.stringify(initialData ?? {}),
    [initialData],
  );
  const stableInitialData = useMemo(
    () => initialData ?? {},
    [initialDataSignature],
  );
  const currentUser = useAtomValue(userAtom);
  const [isValidating, setIsValidating] = useState(false);
  const [isFormSettling, setIsFormSettling] = useState(true);
  const hasUnlockedFormRef = useRef(false);
  const [selectedApplicant, setSelectedApplicant] = useAtom(
    selectedApplicantAtom,
  );
  const defaultValueLoadingIds = useAtomValue(defaultValueLoadingIdsAtom);

  const visibleFormSchema = useMemo(
    () => buildVisibleFormSchema(formSchema),
    [formSchema],
  );
  const schemaSignature = useMemo(
    () => JSON.stringify(visibleFormSchema ?? {}),
    [visibleFormSchema],
  );
  const formDraftCacheKey = useMemo(
    () =>
      draftCacheKey ??
      getApplicationFormDraftCacheKey({
        bindingId,
        formId: form.id,
        schemaSignature,
      }),
    [bindingId, draftCacheKey, form.id, schemaSignature],
  );

  const setIStore = useSetAtom(interpreterStoreAtom);
  const setRuntimeApplication = useSetAtom(runtimeApplicationAtom);
  const setRuntimeForm = useSetAtom(runtimeFormAtom);
  const isBusy = isSubmitting || isValidating || isFormSettling;

  const { getCompiledSchema } = useCodeHelper({
    formSchema: visibleFormSchema,
    formData: stableInitialData,
    application,
  });
  const {
    tableKeys: expressionMasterDataTableKeys,
    cache: expressionMasterDataCache,
  } = usePreloadMasterDataForExpressions(visibleFormSchema);
  const isExpressionMasterDataLoading = expressionMasterDataTableKeys.some(
    (tableKey) => {
      const status = expressionMasterDataCache[tableKey]?.status;
      return !status || status === "idle" || status === "loading";
    },
  );

  const rawCompiledSchema = useMemo(
    () => getCompiledSchema(),
    [getCompiledSchema, expressionMasterDataCache],
  );
  const compiledSchemaSignature = useMemo(
    () => JSON.stringify(rawCompiledSchema ?? {}),
    [rawCompiledSchema],
  );
  const compiledSchema = useMemo(
    () => rawCompiledSchema,
    [compiledSchemaSignature],
  );
  const cachedDraftValues =
    applicationFormDrafts.get(formDraftCacheKey)?.values;
  const effectiveInitialData = useMemo(
    () => ({
      ...stableInitialData,
      ...(cachedDraftValues ?? {}),
    }),
    [cachedDraftValues, stableInitialData],
  );

  const interpreterStore = useInterpreterStore(
    basicFormBuilder,
    compiledSchema,
    {
      initialData: effectiveInitialData,
      events: {
        onEntityValueUpdated(payload) {
          interpreterStore.validateEntityValue(payload.entityId);
        },
      },
    },
  );

  interpreterStore.getData();

  const wrappedComponents = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(entitiesComponents).map(([key, Component]) => [
          key,
          function FieldWithLoading(props: any) {
            const ids = useAtomValue(defaultValueLoadingIdsAtom);
            return (
              <div className="relative">
                {ids.has(props.entity.id) && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white/60" />
                )}
                <Component {...props} />
              </div>
            );
          },
        ]),
      ) as typeof entitiesComponents,
    [],
  );

  useEffect(() => {
    initiateValidatorStore(compiledSchema);
  }, [form.schema]);

  useEffect(() => {
    setIStore((prev) => (prev === interpreterStore ? prev : interpreterStore));
  }, [interpreterStore, setIStore]);

  useEffect(() => {
    setRuntimeApplication(application);
  }, [application, setRuntimeApplication]);

  useEffect(() => {
    setRuntimeForm({ ...form, bindingId });
  }, [bindingId, form, setRuntimeForm]);

  useEffect(() => {
    setSelectedApplicant(currentUser);
    return () => {
      setSelectedApplicant(null);
    };
  }, [setSelectedApplicant, currentUser]);

  useEffect(() => {
    const hasHydratedCurrentForm =
      appliedInitialDataKeyRef.current === formDraftCacheKey;

    if (hasHydratedCurrentForm) {
      applicationFormDrafts.set(formDraftCacheKey, {
        values: interpreterStore.getEntitiesValues(),
      });
      return;
    }

    const nextEntitiesValues = {
      ...interpreterStore.getEntitiesValues(),
      ...stableInitialData,
      ...(applicationFormDrafts.get(formDraftCacheKey)?.values ?? {}),
    };

    interpreterStore.setData({
      entitiesValues: nextEntitiesValues,
      entitiesErrors: {},
    });
    applicationFormDrafts.set(formDraftCacheKey, {
      values: nextEntitiesValues,
    });
    appliedInitialDataKeyRef.current = formDraftCacheKey;
  }, [formDraftCacheKey, interpreterStore, stableInitialData]);

  useReactiveDefaultValues(interpreterStore, visibleFormSchema);

  useEffect(() => {
    if (hasUnlockedFormRef.current) {
      return;
    }

    setIsFormSettling(true);

    if (isExpressionMasterDataLoading || defaultValueLoadingIds.size > 0) {
      return;
    }

    hasUnlockedFormRef.current = true;
    setIsFormSettling(false);
  }, [
    interpreterStore,
    compiledSchema,
    stableInitialData,
    isExpressionMasterDataLoading,
    defaultValueLoadingIds,
  ]);

  useEffect(() => {
    const updateDirty = () => {
      const currentData = interpreterStore.getEntitiesValues();
      const nextDirty = JSON.stringify(currentData) !== initialDataSignature;
      setIsDirty?.((prev) => (prev === nextDirty ? prev : nextDirty));
      applicationFormDrafts.set(formDraftCacheKey, {
        values: currentData,
      });
    };

    updateDirty();

    const unsubscribe = interpreterStore.subscribe((_data, events) => {
      updateDirty();
    });

    return unsubscribe;
  }, [formDraftCacheKey, initialDataSignature, interpreterStore, setIsDirty]);

  async function handleSubmit() {
    if (isValidating || isSubmitting) return;
    try {
      await flushFormCommits();
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("toast.validation_failed"),
        description:
          error instanceof Error
            ? error.message
            : t("toast.fill_out_form_correctly"),
      });
      return;
    }

    setIsValidating(true);
    try {
      const result = await interpreterStore.validateEntitiesValues();
      const data = interpreterStore.getData();

      const registryErrors = await executeAllRegistry(
        data.entitiesValues,
        interpreterStore.schema,
      );
      const localErrors = await executeAllLocalValidator(data.entitiesValues);
      const formErrors = await executeFormValidator();

      const mergedErrors: Record<string, string> = {
        ...localErrors,
        ...registryErrors,
      };
      const hasEntityErrors = Object.keys(mergedErrors).length > 0;
      const hasFormErrors = Object.keys(formErrors).length > 0;

      if (hasEntityErrors) {
        const entitiesErrors = Object.entries(mergedErrors).reduce(
          (acc, [entityId, message]) => {
            acc[entityId] = new ZodError([
              { code: "custom", message, path: [] },
            ]);
            return acc;
          },
          {} as Record<string, ZodError>,
        );
        interpreterStore.setEntitiesErrors(entitiesErrors);
      }

      if (hasFormErrors) {
        const formErrorMessage = Object.values(formErrors)[0];
        toast({
          variant: "destructive",
          title: t("toast.validation_failed"),
          description: formErrorMessage ?? t("toast.fill_out_form_correctly"),
        });
      }

      if (result.success && !hasEntityErrors && !hasFormErrors) {
        onSave(interpreterStore.getData(), isDirty);
        return;
      }

      if (!result.success && !hasEntityErrors && !hasFormErrors) {
        console.warn(result);
        toast({
          variant: "destructive",
          title: t("toast.validation_failed"),
          description: t("toast.fill_out_form_correctly"),
        });
      }
    } finally {
      setIsValidating(false);
    }
  }

  async function handleSaveDraft() {
    if (isValidating || isSubmitting) return;

    setIsValidating(true);
    try {
      await flushFormCommits();
      onSaveDraft(interpreterStore.getData(), isDirty);
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("toast.validation_failed"),
        description:
          error instanceof Error
            ? error.message
            : t("toast.fill_out_form_correctly"),
      });
    } finally {
      setIsValidating(false);
    }
  }

  const applicantSource = form.applicantSource ?? "selection";

  return (
    <>
      {form.description && (
        <p className="text-gray-600 mb-6 whitespace-pre-wrap">
          {form.description}
        </p>
      )}
      <div className="relative">
        {(isFormSettling || isBusy) && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-md bg-white/70">
            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
            <span className="text-sm text-gray-600">{t("loading")}</span>
          </div>
        )}
        <div
          className={
            isFormSettling || isBusy
              ? "pointer-events-none select-none opacity-60"
              : undefined
          }
          aria-busy={isFormSettling || isBusy}
        >
          <div className="flex flex-col gap-4">
            <ApplicationSummary expression={form.summary?.expression} />
            <div className="flex gap-4">
              {/* Applicant — user search picker */}
              <div className="flex-1 flex flex-col gap-2.5">
                <p className="text-base font-medium text-gray-900">
                  {t("application_page.applicant")}
                </p>
                {applicantSource === "selection" ? (
                  <UserSelect
                    value={selectedApplicant || undefined}
                    onValueChange={(_id, user) => {
                      setSelectedApplicant(user ?? null);
                      onApplicantChange?.(user ?? null);
                    }}
                    placeholder={t("application_page.applicant_placeholder")}
                  />
                ) : (
                  <div className="flex h-12 items-center px-5 py-3 bg-gray-100 border border-stroke rounded-md">
                    <span className="flex-1 text-base text-gray-900">
                      {currentUser?.name}
                    </span>
                  </div>
                )}
              </div>

              {/* Submitter — readonly, current user */}
              <div className="flex-1 flex flex-col gap-2.5">
                <p className="text-base font-medium text-gray-900">
                  {t("application_page.submitter")}
                </p>
                <div className="flex h-12 items-center px-5 py-3 bg-gray-100 border border-stroke rounded-md">
                  <span className="flex-1 text-base text-gray-900">
                    {currentUser?.name}
                  </span>
                </div>
              </div>
            </div>
            <hr className="w-full my-6 border-stroke" />
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
            onKeyDownCapture={(e) => {
              if (e.key !== "Enter") return;

              const target = e.target as HTMLElement | null;
              if (!target) return;

              if (target.tagName.toLowerCase() === "textarea") return;

              e.preventDefault();
            }}
            className="space-y-6"
          >
            <InterpreterEntities
              interpreterStore={interpreterStore}
              components={wrappedComponents}
            />
            <div className="flex pt-4 gap-3 justify-between flex-col md:flex-row">
              <div className="flex flex-row justify-between w-full gap-3">
                <Button
                  variant={"destructive"}
                  className="md:w-fit w-full"
                  type="button"
                  disabled
                >
                  {t("buttons.discard")}
                </Button>
                <Button
                  variant={"secondary"}
                  className="md:w-fit w-full"
                  onClick={handleSaveDraft}
                  type="button"
                  disabled={!isDirty || isBusy}
                  loading={isBusy}
                >
                  Save
                </Button>
              </div>
              <div className="flex flex-row gap-3">
                <Button
                  type="submit"
                  className="md:w-fit w-full"
                  disabled={!isDirty || isBusy}
                  loading={isBusy}
                  onClick={handleSubmit}
                >
                  {isSubmitting ? t("buttons.saving") : t("buttons.submit")}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
